import { ActionIcon, Badge, Box, Button, Card, Divider, Group, Menu, Modal, NumberInput, Select, SimpleGrid, Stack, Switch, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { Plan, PlanBody } from '../api/types';
import { PageActions } from '../components/page-actions';
import { usePlans } from '../components/subscription';
import { useToast } from '../components/Toast';
import { nameOf, tr } from '../lib/i18n';
import { fmtDuration, fmtMoney, fmtNum } from '../lib/labels';
import { bi, BilingualField, BilingualScope, BilingualToolbar, isHalfFilled, missingLang, useBilingualScope, type Bilingual } from '../ui/bilingual';
import { Code, ConfirmModal, EmptyState, ErrorState, Loading } from '../ui/kit';

const EMPTY: Omit<PlanBody, 'nameAr' | 'nameEn' | 'description' | 'descriptionEn'> & { code: string } = { code: '', price: 0, currency: 'JOD', durationCount: 1, durationUnit: 'MONTH', isActive: true, sortOrder: 0 };

function PlanDialog(props: { open: boolean; onClose: () => void; plan: Plan | null }) {
  return (
    <Modal opened={props.open} onClose={props.onClose} size={560} title={props.plan ? tr(`تعديل الباقة: ${props.plan.name_ar}`, `Edit plan: ${nameOf(props.plan)}`) : tr('باقة جديدة', 'New plan')}>
      <BilingualScope key={`${props.open}-${props.plan?.id ?? 'new'}`}>
        <PlanForm {...props} />
      </BilingualScope>
    </Modal>
  );
}

function PlanForm({ open, onClose, plan }: { open: boolean; onClose: () => void; plan: Plan | null }) {
  const scope = useBilingualScope();
  const [name, setName] = useState<Bilingual>(bi());
  const [desc, setDesc] = useState<Bilingual>(bi());
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof typeof EMPTY>(k: K, v: (typeof EMPTY)[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setName(bi(plan?.name_ar, plan?.name_en));
    setDesc(bi(plan?.description, plan?.description_en));
    setF(plan ? { code: plan.code, price: Number(plan.price), currency: plan.currency, durationCount: plan.duration_count, durationUnit: plan.duration_unit, isActive: plan.is_active, sortOrder: plan.sort_order } : EMPTY);
  }, [open, plan]);

  const save = useMutation({
    mutationFn: () => {
      const { code, ...rest } = f;
      const body: PlanBody = { ...rest, nameAr: name.ar.trim(), nameEn: name.en.trim(), description: desc.ar.trim() || null, descriptionEn: desc.ar.trim() ? desc.en.trim() || null : null };
      return plan ? api(`/plans/${plan.id}`, { method: 'PATCH', body }) : api('/plans', { method: 'POST', body: { code, ...body } });
    },
    onSuccess: () => {
      toast(plan ? tr('تم حفظ الباقة', 'Plan saved') : tr('تمت إضافة الباقة', 'Plan added'));
      qc.invalidateQueries({ queryKey: ['plans'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError) setErrors(e.fieldErrors());
      toastError(e);
    },
  });

  return (
    <Stack gap="md">
      <BilingualToolbar values={[{ value: name }, { value: desc, required: false }]} />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <BilingualField label={tr('اسم الباقة', 'Plan name')} value={name} onChange={setName} errorAr={errors.nameAr} errorEn={errors.nameEn} placeholder={tr('مثلاً: شهري', 'e.g. Monthly')} />
        <TextInput label={tr('الكود', 'Code')} value={f.code} disabled={!!plan} dir="ltr" withAsterisk={!plan}
          onChange={(e) => set('code', e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
          error={errors.code} description={plan ? tr('ثابت لا يتغير', 'Fixed, cannot be changed') : tr('بالإنجليزية، مثلاً GOLD_YEARLY', 'In English, e.g. GOLD_YEARLY')} />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <NumberInput label={tr('السعر', 'Price')} value={f.price} min={0} decimalScale={3} thousandSeparator="," dir="ltr" onChange={(v) => set('price', Number(v) || 0)} />
        <TextInput label={tr('العملة', 'Currency')} value={f.currency} dir="ltr" onChange={(e) => set('currency', e.currentTarget.value.toUpperCase().slice(0, 3))} />
        <NumberInput label={tr('المدة', 'Duration')} value={f.durationCount} min={1} allowDecimal={false} dir="ltr" onChange={(v) => set('durationCount', Math.max(1, Math.floor(Number(v) || 1)))} />
        <Select label={tr('الوحدة', 'Unit')} value={f.durationUnit} allowDeselect={false} onChange={(v) => set('durationUnit', (v ?? 'MONTH') as 'DAY' | 'MONTH')}
          data={[{ value: 'MONTH', label: tr('شهر', 'Month') }, { value: 'DAY', label: tr('يوم', 'Day') }]} />
      </SimpleGrid>
      <Text size="xs" c="dimmed" mt={-8}>{tr('المدة:', 'Duration:')} {fmtDuration(f.durationCount || 1, f.durationUnit)}</Text>
      <BilingualField label={tr('وصف', 'Description')} required={false} value={desc} onChange={setDesc} multiline minRows={2} errorAr={errors.description} errorEn={errors.descriptionEn} description={tr('اختياري', 'Optional')} />
      <Group gap="lg" align="flex-end">
        <NumberInput label={tr('الترتيب', 'Sort order')} w={120} value={f.sortOrder} allowDecimal={false} dir="ltr" onChange={(v) => set('sortOrder', Math.floor(Number(v) || 0))} />
        <Switch mb={8} checked={f.isActive} onChange={(e) => set('isActive', e.currentTarget.checked)}
          label={f.isActive ? tr('مفعّلة — تظهر عند التجديد', 'Active — shown on renewal') : tr('موقوفة — لا تظهر لاشتراكات جديدة', 'Inactive — not offered for new subscriptions')} />
      </Group>
      <Group justify="flex-end" gap="xs" mt="xs">
        <Button variant="default" onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button
          loading={save.isPending}
          disabled={!plan && f.code.length < 2}
          onClick={() => {
            const miss = missingLang(name) ?? (isHalfFilled(desc) ? missingLang(desc, false) : null);
            if (miss) return scope?.setLang(miss);
            save.mutate();
          }}
        >
          {plan ? tr('حفظ التعديلات', 'Save changes') : tr('إضافة الباقة', 'Add plan')}
        </Button>
      </Group>
    </Stack>
  );
}

function PlanCard({ p, onEdit, onDelete, onToggle, toggling }: { p: Plan; onEdit: () => void; onDelete: () => void; onToggle: () => void; toggling: boolean }) {
  const used = !!(p.organizations_count || p.payments_count);
  const free = Number(p.price) === 0;
  return (
    <Card padding={0} style={{ opacity: p.is_active ? 1 : 0.62, display: 'flex', flexDirection: 'column' }}>
      <Box p="lg" pb="md" style={{ flex: 1 }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
          <Box style={{ minWidth: 0 }}>
            <Text fw={600} size="md" lh={1.3}>{p.name_ar}</Text>
            <Group gap={6} mt={2} wrap="nowrap">
              {p.name_en ? <Text size="xs" c="dimmed" dir="ltr" truncate>{p.name_en}</Text> : <Text size="xs" c="orange.7">{tr('بدون إنجليزي', 'No English')}</Text>}
              <Code>{p.code}</Code>
            </Group>
          </Box>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target><ActionIcon aria-label={tr('خيارات', 'Options')}><IconDots size={18} /></ActionIcon></Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={15} />} onClick={onEdit}>{tr('تعديل', 'Edit')}</Menu.Item>
              <Tooltip label={tr('مستخدمة — أوقفها بدل الحذف', 'In use — deactivate it instead')} disabled={!used} position="left">
                <Menu.Item color="red" leftSection={<IconTrash size={15} />} disabled={used} onClick={onDelete}>{tr('حذف', 'Delete')}</Menu.Item>
              </Tooltip>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Group gap={6} align="baseline" mt="lg">
          {free ? (
            <Text fz={30} fw={700} lh={1}>{tr('مجانية', 'Free')}</Text>
          ) : (
            <Text fz={30} fw={700} lh={1} className="pf-num">{fmtMoney(p.price, p.currency)}</Text>
          )}
          <Text size="sm" c="dimmed">/ {fmtDuration(p.duration_count, p.duration_unit)}</Text>
        </Group>
        {(p.description || p.description_en) && (
          <Text size="sm" c="dimmed" mt="sm" lineClamp={3}>{p.description}{p.description_en && <><br /><bdi>{p.description_en}</bdi></>}</Text>
        )}
      </Box>
      <Divider color="var(--pf-line)" />
      <Group justify="space-between" px="lg" py="sm" gap="xs">
        <Group gap="lg">
          <Box><Text size="xs" c="dimmed">{tr('الشركات', 'Companies')}</Text><Text fw={600} className="pf-num">{fmtNum(p.organizations_count)}</Text></Box>
          <Box><Text size="xs" c="dimmed">{tr('الدفعات', 'Payments')}</Text><Text fw={600} className="pf-num">{fmtNum(p.payments_count)}</Text></Box>
        </Group>
        <Group gap={8}>
          <Badge variant="light" color={p.is_active ? 'teal' : 'gray'}>{p.is_active ? tr('مفعّلة', 'Active') : tr('موقوفة', 'Inactive')}</Badge>
          <Switch checked={p.is_active} onChange={onToggle} disabled={toggling} aria-label={tr('تفعيل الباقة', 'Activate plan')} />
        </Group>
      </Group>
    </Card>
  );
}

/** Subscription plans — fully editable; a used plan can only be deactivated (history keeps its name). */
export function PlansPage() {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const plans = usePlans();
  const [editing, setEditing] = useState<Plan | null | 'new'>(null);
  const [deleting, setDeleting] = useState<Plan | null>(null);

  const toggle = useMutation({
    mutationFn: (p: Plan) => api(`/plans/${p.id}`, { method: 'PATCH', body: { isActive: !p.is_active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plans'] }),
    onError: toastError,
  });
  const del = useMutation({
    mutationFn: (p: Plan) => api(`/plans/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(tr('تم حذف الباقة', 'Plan deleted'));
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['plans'] });
    },
    onError: toastError,
  });
  const items = plans.data?.items ?? [];

  return (
    <Stack gap="md">
      <PageActions>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>{tr('باقة جديدة', 'New plan')}</Button>
      </PageActions>
      {plans.isLoading ? (
        <Loading />
      ) : plans.isError ? (
        <ErrorState error={plans.error} onRetry={() => plans.refetch()} />
      ) : items.length === 0 ? (
        <Card><EmptyState title={tr('لا توجد باقات بعد', 'No plans yet')} text={tr('أضف أول باقة لتتمكن من تفعيل اشتراكات الشركات.', 'Add the first plan to start subscribing companies.')} action={<Button mt="xs" leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>{tr('باقة جديدة', 'New plan')}</Button>} /></Card>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {items.map((p) => (
            <PlanCard key={p.id} p={p} onEdit={() => setEditing(p)} onDelete={() => setDeleting(p)} onToggle={() => toggle.mutate(p)} toggling={toggle.isPending} />
          ))}
          <UnstyledButton onClick={() => setEditing('new')} style={{ border: '1.5px dashed var(--pf-line-strong)', borderRadius: 'var(--mantine-radius-lg)', minHeight: 180 }} className="pf-tile">
            <Stack align="center" justify="center" gap={6} h="100%">
              <IconPlus size={22} stroke={1.6} color="var(--pf-muted)" />
              <Text size="sm" c="dimmed" fw={500}>{tr('إضافة باقة', 'Add a plan')}</Text>
            </Stack>
          </UnstyledButton>
        </SimpleGrid>
      )}
      <Text size="xs" c="dimmed">{tr('تغيير السعر يطبَّق على التجديدات القادمة فقط — الدفعات السابقة تحتفظ بمبلغها واسم باقتها.', 'Price changes apply to future renewals only — past payments keep their amount and plan name.')}</Text>
      <PlanDialog open={editing !== null} plan={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      <ConfirmModal open={!!deleting} danger title={tr('حذف الباقة', 'Delete plan')} text={tr(`حذف «${deleting?.name_ar}» نهائياً؟`, `Permanently delete "${nameOf(deleting)}"?`)} busy={del.isPending}
        onClose={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting)} confirmLabel={tr('حذف', 'Delete')} />
    </Stack>
  );
}
